<template>
  <Loader v-if="loader" />
  <div class="table-container">

    <div class="card p-3">
      <div class="grid">
        <div class="col-12 lg:col-10">
          <label>Mese (decorrenza o invio pratica)</label>
          <Calendar v-model="selectedDate" dateFormat="mm-yy" view="month" showIcon class="inputfield w-full mt-1" />
        </div>
        <div class="col-12 lg:col-2 flex align-items-end">
          <Button label="Cerca" @click="getData" icon="pi pi-search" iconPos="left" class="w-full" />
        </div>
      </div>
    </div>

    <div v-show="selectedItems.length > 0" class="btn-selected">
      <Button icon="pi pi-plus" label="Aggiungi voci alla fatturazione" @click="addCosti"
        :badge="selectedItems.reduce((a, v) => a + v.costo_imponibile, 0).toFixed(2)" class="p-button-warning p-3" />
    </div>

    <div class="card">
      <DataTable ref="dt" :value="items" :filters="filters" v-model:selection="selectedItems" editMode="cell"
        @cell-edit-complete="onCellEditComplete" responsiveLayout="scroll">
        <template #header>
          <div class="flex justify-content-between flex-wrap">
            <div class="flex align-items-center justify-content-center mb-2">
              <span class="p-input-icon-left" icon="pi pi-search"  iconPos="left">
                <InputText type="search" v-model="filters['global'].value" placeholder="Cerca..." />
              </span>
            </div>
            <div class="flex align-items-center justify-content-center">
              <Button icon="pi pi-download" label="Esporta" @click="exportCSV($event)"
                class="p-button-secondary ml-2" />
            </div>
          </div>
        </template>
        <template #empty>
          <div class="text-center">Nessun dato trovato.</div>
        </template>
        <Column selectionMode="multiple" style="width: 3rem" :exportable="false"></Column>
        <Column field="fornitura" header="Fornitura" :sortable="true">
          <template #body="{ data }">
            <p v-if="data.pod_id == 0 && data.pdr_id == 0">
              {{ data.fornitura }}
            </p>
            <p v-else>
              <span v-if="data.pod_id != 0">
                <router-link :to="`/ask/pods/${data.pod_id}/dettagli`" target="_blank">
                  {{ data.fornitura }}
                </router-link>
              </span>
              <span v-else>
                <router-link :to="`/ask/pdrs/${data.pdr_id}/dettagli`" target="_blank">
                  {{ data.fornitura }}
                </router-link>
              </span>
            </p>
          </template>
        </Column>
        <Column field="type" header="Tipo" :sortable="true"></Column>
        <Column field="data_fatturabile_da" header="Data Fatturabile Da (editabile)" :sortable="true"
          style="min-width:180px">
          <template #editor="{ data, field }">
            <Calendar v-model="data[field]" view="month" :showIcon="true" dateFormat="dd-mm-yy" />
          </template>
          <template #body="{ data }">
            <span class="cursor-pointer">
              {{ moment(data.data_fatturabile_da).format('MM YYYY') }}
            </span>
          </template>
        </Column>
        <Column field="data_fatturabile_a" header="Data Fatturabile A (editabile)" :sortable="true"
          style="min-width:180px">
          <template #editor="{ data, field }">
            <Calendar v-model="data[field]" view="month" :showIcon="true" dateFormat="dd-mm-yy" />
          </template>
          <template #body="{ data }">
            <span class="cursor-pointer">
              {{ moment(data.data_fatturabile_a).format('MM YYYY') }}
            </span>
          </template>
        </Column>
        <Column field="voce_fattura" header="Voce Fattura (editabile)" style="min-width:160px">
          <template #editor="{ data, field }">
            <Dropdown v-model="data[field]" :options="fatturaVoci" :filter="true" optionLabel="code"
              placeholder="Seleziona">
              <template #option="slotProps">
                <span>{{ slotProps.option.code }}</span>
              </template>
            </Dropdown>
          </template>
          <template #body="{ data }">
            <span class="cursor-pointer">
              {{ data.voce_fattura.code }}
            </span>
          </template>
        </Column>
        <Column field="aliquota_iva" header="Aliquota Iva (editabile)" style="min-width:60px">
          <template #editor="{ data, field }">
            <Dropdown v-model="data[field]" :options="typeIva" optionLabel="name" placeholder="Seleziona">
              <template #option="slotProps">
                <span>{{ slotProps.option.name }}</span>
              </template>
            </Dropdown>
          </template>
          <template #body="{ data }">
            <span class="cursor-pointer">
              {{ data.aliquota_iva.name }}
            </span>
          </template>
        </Column>
        <Column field="descrizione" header="Descrizione (editabile)" :sortable="true" style="min-width:180px">
          <template #editor="{ data, field }">
            <Textarea v-model="data[field]" :autoResize="true" rows="5" cols="30" />
          </template>
          <template #body="{ data }">
            <span class="cursor-pointer">
              {{ data.descrizione }}
            </span>
          </template>
        </Column>
        <Column field="cliente" header="Cliente" :sortable="true"></Column>
        <Column field="piva" header="P.iva" :sortable="true"></Column>
        <Column field="codfis" header="C.fiscale" :sortable="true"></Column>
        <Column field="rappresentante" header="Rappresentante" :sortable="true"></Column>
        <Column field="agente" header="Agente" :sortable="true"></Column>
        <Column field="cod_servizio" header="Code" :sortable="true"></Column>

        <Column field="provider" header="Provider" :sortable="true" class="provider"></Column>
        <Column field="cod_pratica" header="Codice pratica interno"></Column>
        <Column field="cod_pratica_provider" header="Codice pratica provider"></Column>
        <Column field="costo_imponibile" header="Imponibile" :sortable="true"></Column>
        <Column field="costo_iva" header="Iva" :sortable="true"></Column>
        <Column field="costo_totale" header="Totale" :sortable="true"></Column>
        <Column field="data_pratica" header="Data Pratica" :sortable="true">
          <template #body="slotProps">
            {{ formatDate(slotProps.data.data_pratica) }}
          </template>
        </Column>
        <Column field="data_decorrenza" header="Data Decorrenza" :sortable="true">
          <template #body="slotProps">
            {{ formatDate(slotProps.data.data_decorrenza) }}
          </template>
        </Column>
        <Column field="data_esito" header="Data Esito" :sortable="true">
          <template #body="slotProps">
            {{ formatDate(slotProps.data.data_esito) }}
          </template>
        </Column>
        <Column field="esito_pratica" header="Esito" :sortable="true"></Column>
        <Column field="motivazione_esito" header="Motivazione Esito" :sortable="true"></Column>
        <ColumnGroup type="footer">
          <Row>
            <Column footer="Totale Imponibile:" :colspan="1" footerStyle="text-align:right" />
            <Column :footer="totalCosti" />
            <Column :colspan="24" />
          </Row>
        </ColumnGroup>
      </DataTable>
    </div>
  </div>


  <!-- Dialog Report -->
  <Dialog header="Report" v-model:visible="dialogReport" :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
    :style="{ width: '70vw' }" :modal="true">
    <div class="report" v-if="report && report.length">
      <div class="mt-2">
        <DataTable :value="report" tableStyle="width: 100%">
          <Column field="code" header="Fornitura"></Column>
          <Column field="month" header="Mese">
            <template #body="{ data }">
              {{ formatDate(data.month) }}
            </template>
          </Column>
          <Column header="Esito">
            <template #body="{ data }">
              <ul v-if="data.error">
                <li v-for="err in data.error" :key="err" class="p-error">
                  {{ err }}
                </li>
              </ul>
              <ul v-else>
                <li> <span :class="[data.success ? 'text-green-500' : 'p-error']">{{ data.msg }}</span></li>
              </ul>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>
    <template #footer>
      <Button label="Chiudi" icon="pi pi-times" @click="dialogReport = false" class="p-button-text" />
    </template>
  </Dialog>

</template>

<script>


import { FilterMatchMode } from 'primevue/api';
import Service from '@/services/ask-pratiche/pratiche-service'
import UtilityService from '@/services/ask-management/utility-service'
import { ref, getCurrentInstance, computed, onMounted } from 'vue';
import Loader from '@/components/Loader.vue'
import moment from 'moment';
import { useToast } from "primevue/usetoast";

export default {
  components: {
    Loader
  },
  setup() {
    const dt = ref();
    const toast = useToast();
    const loader = ref(false);
    const items = ref([]);
    const selectedDate = ref(new Date())
    const selectedItems = ref([]);
    const fatturaVoci = ref()
    const typeIva = ref()
    const report = ref()
    const dialogReport = ref(false)
    const filters = ref({
      'global': { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    const self = getCurrentInstance();
    self.$eventBus = self.appContext.config.globalProperties.$eventBus
    self.$http = self.appContext.config.globalProperties.$http
    const formatDate = self.appContext.config.globalProperties.$formatDate
    const service = new Service(self.$http, self.$eventBus)
    const utilityService = new UtilityService(self.$http, self.$eventBus)

    onMounted(() => {
      getFatturaVoci()
      getTypeIva()
    })

    const getFatturaVoci = () => {
      utilityService.getFatturaVoci()
        .then((res) => {
          fatturaVoci.value = res.items.data
        })
    }

    const getTypeIva = () => {
      utilityService.getTipiAliquotaIva()
        .then((res) => {
          typeIva.value = res.items
        })
    }

    const getData = () => {
      report.value = null
      loader.value = true;
      let d = moment(selectedDate.value).format('YYYY-MM-DD')
      service.getCostiPratiche(d)
        .then((res) => {
          calculateAliquotaIva(res.items)
        }).finally(() => {
          loader.value = false;
        })
    }

    const calculateAliquotaIva = (els) => {
      els.forEach(el => {
        let ivaPercentage = Math.round((el.costo_iva / el.costo_imponibile) * 100);
        let ivaObj = typeIva.value.filter(el => el.name == ivaPercentage)[0] ?? {}
        if ('id' in ivaObj) {
          el.aliquota_iva = ivaObj
        }
      });
      items.value = els
    }

    const addCosti = () => {
      if (validateAddCosti()) {
        loader.value = true;
        service.addCostiPratiche(selectedItems.value)
          .then((res) => {
            if (res.items.length > 0) {
              report.value = res.items
              dialogReport.value = true
            }
          }).finally(() => {
            loader.value = false;
          })
      }
    }

    const validateAddCosti = () => {
      let valid = true;
      selectedItems.value.forEach(el => {
        if (el.voce_fattura.id == 0) {
          valid = false
          toast.add({ severity: 'warn', summary: 'Voce Fattura', detail: `Voce fattura per ${el.fornitura} non è stata selezionata` });
        }
        if (moment(el.data_fatturabile_da).isAfter(moment(el.data_fatturabile_a))) {
          valid = false
          toast.add({ severity: 'warn', summary: 'Temporalità errata', detail: `Data fatturabile Da è successiva a Data Fatturabile A per ${el.fornitura}` });
        }
        if (el.aliquota_iva.id == 0) {
          valid = false
          toast.add({ severity: 'warn', summary: 'Aliquota Iva', detail: `Aliquota Iva per ${el.fornitura} non è stata selezionata` });
        }
      });
      return valid
    }

    const totalCosti = computed(() => {
      let total = 0;
      for (let item of items.value) {
        total += item.costo_imponibile;
      }

      return formatCurrency(total);
    });

    const onCellEditComplete = (event) => {
      items.value[event.index][event.field] = event.newValue
    }

    // watch(items, (newItems, oldItems) => {
    //   console.log(newItems, oldItems);
    // },{ deep: true })

    const exportCSV = () => {
      dt.value.exportCSV();
    };

    const formatCurrency = (value) => {
      return value.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
    };


    return {
      dt,
      items,
      selectedDate,
      selectedItems,
      loader,
      formatDate,
      filters,
      getData,
      totalCosti,
      addCosti,
      fatturaVoci,
      typeIva,
      report,
      dialogReport,
      exportCSV,
      onCellEditComplete,
      moment
    }
  }
}
</script>

<style scoped>
.btn-selected {
  position: fixed;
  bottom: 60px;
  right: 340px;
  z-index: 2;
}

</style>
